import type {
  SuggestionItem,
  SuggestionMenuProps,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-utils/suggestion-menu/suggestion-menu-types';
import { calculateStartPosition } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-utils/suggestion-menu/suggestion-menu-utils';
import { useFloatingElement } from '@examdojo/core/tiptap-react-ui-components/hooks/use-floating-element';
import { useMenuNavigation } from '@examdojo/core/tiptap-react-ui-components/hooks/use-menu-navigation';
import { useTiptapEditor } from '@examdojo/core/tiptap-react-ui-components/hooks/use-tiptap-editor';
import { flip, offset, shift, size } from '@floating-ui/react';
import { PluginKey } from '@tiptap/pm/state';
import type { Range } from '@tiptap/react';
import { Suggestion, type SuggestionKeyDownProps, SuggestionPluginKey, type SuggestionProps } from '@tiptap/suggestion';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A component that renders a suggestion menu for Tiptap editors.
 * Displays a floating menu when a trigger character is typed.
 */
export const SuggestionMenu = ({
  editor: providedEditor,
  floatingOptions,
  selector = 'tiptap-suggestion-menu',
  children,
  maxHeight = 384,
  pluginKey = SuggestionPluginKey,
  ...internalSuggestionProps
}: SuggestionMenuProps) => {
  const { editor } = useTiptapEditor(providedEditor);

  const [show, setShow] = useState<boolean>(false);

  // If later we want the floating stick to the position while browser is scrolling,
  // we can uncomment this part and pass the getBoundingClientRect prop to FloatingElement instead of referenceElement.
  // const [internalClientRect, setInternalClientRect] = useState<DOMRect | null>(
  //   null
  // )
  const [internalDecorationNode, setInternalDecorationNode] = useState<HTMLElement | null>(null);
  const [internalCommand, setInternalCommand] = useState<((item: SuggestionItem) => void) | null>(null);
  const [internalItems, setInternalItems] = useState<SuggestionItem[]>([]);
  const [internalQuery, setInternalQuery] = useState<string>('');
  const [, setInternalRange] = useState<Range | null>(null);

  const { ref, style, getFloatingProps, isMounted } = useFloatingElement(show, internalDecorationNode, 1000, {
    placement: 'bottom-start',
    middleware: [
      offset(10),
      flip({
        mainAxis: true,
        crossAxis: false,
      }),
      shift(),
      size({
        apply({ availableHeight, elements }) {
          if (elements.floating) {
            const maxHeightValue = maxHeight ? Math.min(maxHeight, availableHeight) : availableHeight;

            elements.floating.style.setProperty('--suggestion-menu-max-height', `${maxHeightValue}px`);
          }
        },
      }),
    ],
    onOpenChange(open) {
      if (!open) {
        setShow(false);
      }
    },
    ...floatingOptions,
  });

  const internalSuggestionPropsRef = useRef(internalSuggestionProps);

  useEffect(() => {
    internalSuggestionPropsRef.current = internalSuggestionProps;
  }, [internalSuggestionProps]);

  const closePopup = useCallback(() => {
    setShow(false);
  }, []);

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }

    const existingPlugin = editor.state.plugins.find((plugin) => plugin.spec.key === pluginKey);
    if (existingPlugin) {
      editor.unregisterPlugin(pluginKey);
    }

    const suggestion = Suggestion({
      pluginKey: pluginKey instanceof PluginKey ? pluginKey : new PluginKey(pluginKey),
      editor,

      allow(props) {
        try {
          // Validate range
          if (props.range.from < 0 || props.range.to > editor.state.doc.content.size) {
            return false;
          }

          const $from = editor.state.doc.resolve(props.range.from);

          // Check if we're inside an image node
          for (let depth = $from.depth; depth > 0; depth--) {
            if ($from.node(depth).type.name === 'image') {
              return false; // Don't allow slash command inside image (since we support captions)
            }
          }

          // Only allow emoji dropdown when explicitly triggered (e.g. from slash menu),
          // not when user manually types ":"
          if (pluginKey === 'emojiDropdownMenu') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (!(editor.storage as any)._emojiExplicitTrigger) {
              return false;
            }
          }

          return true;
        } catch (e) {
          console.warn('Error in suggestion allow check:', e);
          return false;
        }
      },

      command({ editor, range, props }) {
        if (!range) {
          return;
        }

        const { view, state } = editor;
        const { selection } = state;

        const isMention = editor.extensionManager.extensions.some((extension) => {
          const name = extension.name;
          return name === 'mention' && extension.options?.suggestion?.char === internalSuggestionPropsRef.current.char;
        });

        if (!isMention) {
          const cursorPosition = selection.$from.pos;
          const previousNode = selection.$head?.nodeBefore;

          const startPosition = previousNode
            ? calculateStartPosition(cursorPosition, previousNode, internalSuggestionPropsRef.current.char)
            : selection.$from.start();

          const transaction = state.tr.deleteRange(startPosition, cursorPosition);
          view.dispatch(transaction);
        }

        const nodeAfter = view.state.selection.$to.nodeAfter;
        const overrideSpace = nodeAfter?.text?.startsWith(' ');

        const rangeToUse = { ...range };

        if (overrideSpace) {
          rangeToUse.to += 1;
        }

        props.onSelect({ editor, range: rangeToUse, context: props.context });
      },

      render: () => {
        return {
          onStart: (props: SuggestionProps<SuggestionItem>) => {
            setInternalDecorationNode((props.decorationNode as HTMLElement) ?? null);
            setInternalCommand(() => props.command);
            setInternalItems(props.items);
            setInternalQuery(props.query);
            setInternalRange(props.range);
            // setInternalClientRect(props.clientRect?.() ?? null)
            setShow(true);
          },

          onUpdate: (props: SuggestionProps<SuggestionItem>) => {
            setInternalDecorationNode((props.decorationNode as HTMLElement) ?? null);
            setInternalCommand(() => props.command);
            setInternalItems(props.items);
            setInternalQuery(props.query);
            setInternalRange(props.range);
            // setInternalClientRect(props.clientRect?.() ?? null)
          },

          onKeyDown: (props: SuggestionKeyDownProps) => {
            if (props.event.key === 'Escape') {
              closePopup();
              return true;
            }
            return false;
          },

          onExit: () => {
            setInternalDecorationNode(null);
            setInternalCommand(null);
            setInternalItems([]);
            setInternalQuery('');
            setInternalRange(null);
            // setInternalClientRect(null)
            setShow(false);

            // Clear the explicit trigger flag when the emoji popup closes
            if (pluginKey === 'emojiDropdownMenu') {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (editor.storage as any)._emojiExplicitTrigger = false;
            }
          },
        };
      },
      ...internalSuggestionPropsRef.current,
    });

    editor.registerPlugin(suggestion);

    return () => {
      if (!editor.isDestroyed) {
        editor.unregisterPlugin(pluginKey);
      }
    };
  }, [editor, pluginKey, closePopup]);

  const onSelect = useCallback(
    (item: SuggestionItem) => {
      closePopup();

      if (internalCommand) {
        internalCommand(item);
      }
    },
    [closePopup, internalCommand],
  );

  const { selectedIndex } = useMenuNavigation({
    editor: editor,
    query: internalQuery,
    items: internalItems,
    onSelect,
  });

  if (!isMounted || !show || !editor) {
    return null;
  }

  return (
    <div
      ref={ref}
      style={style}
      {...getFloatingProps()}
      data-selector={selector}
      className="tiptap-suggestion-menu"
      role="listbox"
      aria-label="Suggestions"
      onPointerDown={(e) => e.preventDefault()}
    >
      {children({
        items: internalItems,
        selectedIndex,
        onSelect,
      })}
    </div>
  );
};
